import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemButton,
  Button,
  IconButton,
  Box,
  AppBar,
  Drawer,
  Toolbar,
  Tooltip,
  Divider,
  Typography,
  ListItemText,
  useMediaQuery
} from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { useAppSelector } from '../../../store';
import { environment } from '../../../config/environment';

import { apiService } from '../../../services/api';
import { HeaderProps, MenuItem } from './Header.types';
import { authService, UserRole } from '../../../services/auth.service';
import { Menu as MenuIcon, ManageAccounts, AccountCircle, Info, Logout, Close as CloseIcon } from '@mui/icons-material';

import './Header.css';

const allMenuItems: MenuItem[] = [
  { path: '/pricing-regional', label: 'Ціноутворення', roles: ['Administrator', 'Manager', 'Marketer', 'User'] },
  {
    path: '/pricing-province',
    label: 'Динаміка цін по областях',
    roles: ['Administrator', 'Manager', 'Marketer', 'User']
  },
  { path: '/price-comparison', label: 'Порівняння цін', roles: ['Administrator', 'Manager', 'Marketer'] },
  { path: '/azk-binding', label: "Зв'язування АЗК", roles: ['Administrator', 'Manager', 'Marketer', 'User'] },
  { path: '/azk-list', label: 'Перелік АЗК', roles: ['Administrator', 'Manager', 'Marketer', 'User'] },
  { path: '/upload', label: 'Завантаження', roles: ['Administrator', 'Manager', 'Marketer'] },
  { path: '/price-processing', label: 'Проведення цін', roles: ['Administrator', 'Manager', 'Marketer'] },
  { path: '/scheduler', label: 'Шедулер', roles: ['Administrator', 'Manager'] },
  { path: '/orders', label: 'Розпорядження', roles: ['Administrator', 'Manager', 'Marketer'] },
  { path: '/brands', label: 'Бренди', roles: ['Administrator', 'Manager', 'Marketer', 'User'] }
];

export const Header = ({ user }: HeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobileView = useMediaQuery('(max-width:768px)');
  const msalContext = environment.devAuthOff ? null : useMsal();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [visibleMenuItems, setVisibleMenuItems] = useState<MenuItem[]>([]);
  const [currentUser, setCurrentUser] = useState(user);
  const backendVersion = useAppSelector(state => state.global.backendVersion);
  const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '—';

  useEffect(() => {
    const updateMenuItems = () => {
      // In dev mode, show all menu items
      if (environment.devAuthOff) {
        setVisibleMenuItems(allMenuItems);
        return;
      }

      const currentRole = authService.getUserRole();
      if (currentRole) {
        setVisibleMenuItems(allMenuItems.filter(item => item.roles.includes(currentRole)));
      } else {
        setVisibleMenuItems([]);
      }
    };

    updateMenuItems();
    setCurrentUser(user);
  }, [user]);

  useEffect(() => {
    if (environment.devAuthOff) return;

    const updateMenuOnRouteChange = async () => {
      await authService.checkRoleUpdate();
      const currentRole = authService.getUserRole();
      if (currentRole) {
        setVisibleMenuItems(allMenuItems.filter(item => item.roles.includes(currentRole)));
      }
    };

    updateMenuOnRouteChange();
  }, [location.pathname]);

  const getCurrentUserRole = (): UserRole | null => {
    if (environment.devAuthOff) {
      return user?.role || 'Administrator';
    }
    return authService.getUserRole();
  };

  const getCurrentUserName = (): string => {
    // Dev mode
    if (environment.devAuthOff) {
      return user?.display_name || user?.name || 'Dev User';
    }

    // Production mode - try MSAL first
    if (msalContext) {
      const accounts = msalContext.instance.getAllAccounts();
      if (accounts.length > 0) {
        const account = accounts[0];
        const msalName = account.name || account.username;
        if (msalName) {
          return msalName;
        }
      }
    }

    if (currentUser) {
      return currentUser.name || currentUser.display_name;
    }

    return 'User';
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const logout = () => {
    if (environment.devAuthOff) {
      // In dev mode, just reload the page
      window.location.reload();
    } else {
      authService.logout();
    }
  };

  const isUserManagementVisible = (): boolean => {
    const userRole = getCurrentUserRole();
    return userRole === 'Administrator';
  };

  const showInstructions = async () => {
    const role = getCurrentUserRole();
    if (!role) return;
    try {
      const { blob } = await apiService.getBlob(`/manuals/${role}`);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      // auth errors are handled by apiService interceptor (logout + redirect)
    }
  };

  const handleNavClick = (path: string, event?: React.MouseEvent) => {
    if (event && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
    if (isMobileView) {
      closeMobileMenu();
    }
  };

  return (
    <AppBar position="fixed" className="header-app-bar">
      <Toolbar className="header-toolbar">
        <Box className="header-content">
          <Box className="header-main-container">
            {/* Mobile Menu Toggle Button */}
            {isMobileView && (
              <IconButton onClick={toggleMobileMenu} className="mobile-menu-toggle">
                <MenuIcon />
              </IconButton>
            )}

            {/* Desktop Navigation */}
            {!isMobileView && (
              <Box className="desktop-nav">
                {visibleMenuItems.map(item => {
                  return (
                    <Button
                      key={item.path}
                      onClick={event => handleNavClick(item.path, event)}
                      onMouseDown={event => { if (event.button === 1) { event.preventDefault(); window.open(item.path, '_blank'); } }}
                      className={`nav-button ${location.pathname === item.path ? 'active' : ''}`}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Box>
            )}

            {/* Header Buttons */}
            <Box className="header-buttons">
              {isUserManagementVisible() && (
                <IconButton
                  onClick={() => navigate('/user-management')}
                  title="Управління користувачами"
                  className="header-icon-button"
                >
                  <ManageAccounts />
                </IconButton>
              )}

              <Tooltip
                title={
                  <>
                    <div>{`${getCurrentUserName()}${getCurrentUserRole() ? ` (${getCurrentUserRole()})` : ''}`}</div>
                    <div>{`Версія Веб-додатку: ${appVersion}`}</div>
                    {backendVersion && <div>{`Версія Бек-енд сервісу: ${backendVersion}`}</div>}
                  </>
                }
              >
                <IconButton className="header-icon-button">
                  <AccountCircle />
                </IconButton>
              </Tooltip>

              <IconButton onClick={showInstructions} title="Інструкція" className="header-icon-button">
                <Info />
              </IconButton>

              <IconButton onClick={logout} title="Logout" className="header-icon-button">
                <Logout />
              </IconButton>
            </Box>
          </Box>

          {/* Mobile Navigation Drawer */}
          <Drawer
            anchor="left"
            open={isMobileView && isMobileMenuOpen}
            onClose={closeMobileMenu}
            PaperProps={{
              sx: {
                width: 280,
                backgroundColor: 'rgb(132, 189, 0)',
                color: 'white'
              }
            }}
          >
            <Box className="mobile-drawer-content">
              {/* Drawer Header */}
              <Box className="mobile-drawer-header">
                <Typography variant="h6" sx={{ flexGrow: 1, color: 'white', fontWeight: 500 }}>
                  Меню
                </Typography>
                <IconButton onClick={closeMobileMenu} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />

              {/* Navigation Items */}
              <List sx={{ pt: 1 }}>
                {visibleMenuItems.map(item => (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton
                      onClick={event => handleNavClick(item.path, event)}
                      onMouseDown={event => { if (event.button === 1) { event.preventDefault(); window.open(item.path, '_blank'); } }}
                      className={`mobile-drawer-item ${location.pathname === item.path ? 'active' : ''}`}
                      sx={{
                        py: 1.5,
                        px: 2,
                        backgroundColor: location.pathname === item.path ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          color: 'white',
                          fontWeight: location.pathname === item.path ? 500 : 400,
                          fontSize: '0.95rem'
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)', mt: 2 }} />

              {/* User Info */}
              <Box className="mobile-drawer-footer">
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', px: 2, py: 1 }}>
                  {getCurrentUserName()}
                  {getCurrentUserRole() && (
                    <span style={{ display: 'block', fontSize: '0.75rem' }}>{getCurrentUserRole()}</span>
                  )}
                </Typography>
              </Box>
            </Box>
          </Drawer>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
